import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  GraduationCap, Plus, Search, Filter, ArrowUpDown,
  Edit2, Trash2, Eye, Mail, Phone, BookOpen, X,
  Loader2, Users, CheckCircle, XCircle, Award,
  Calculator, School, Star, User, Calendar, ChevronDown, Check, FileDown, Table2,
  ToggleLeft, ToggleRight, Building2, Upload, Download, FileSpreadsheet,
  AlertTriangle, CheckCircle2, ArrowDownToLine, MapPin, Heart, Baby,
  Briefcase, Home, Image, FileText, Clock
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { teachersApi, classesApi, apiService } from '@/services/api'
import { toast } from 'sonner'
import { useLang } from '@/hooks/useLang'

// ==================== TYPES ====================
interface Teacher {
  id: number
  user_id: number
  school_id: number
  academic_year_id: number
  matricule: string
  qualifications: string[]
  subjects_specialization: string[]
  hire_date: string
  contract_type: string
  status: string
  date_of_birth?: string
  place_of_birth?: string
  gender?: string
  nationality?: string
  profession?: string
  diploma?: string
  specialty?: string
  neighborhood?: string
  city?: string
  address?: string
  marital_status?: string
  number_of_children?: number
  photo_url?: string
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  user?: { id: number; first_name: string; last_name: string; email: string; phone?: string }
  teacher_subject_classes?: TeacherSubjectClass[]
  availabilities?: TeacherAvailability[]
  attachments?: TeacherAttachment[]
}

interface TeacherSubjectClass {
  id: number
  teacher_id: number
  school_id: number
  academic_year_id: number
  subject_id: number
  class_id: number
  hours_per_week: number
  coefficient: number
  is_principal_teacher: boolean
  subject_name?: string
  class_name?: string
  level_name?: string
  section_name?: string
  subject?: { label_fr: string; label_en: string; code: string }
  class_?: { name: string; abbreviation: string; level_name?: string; section_name?: string }
}

interface TeacherAvailability {
  id: number
  teacher_id: number
  day_of_week: number
  time_slot: string
  is_available: boolean
}

interface TeacherAttachment {
  id: number
  teacher_id: number
  file_name: string
  file_path: string
  file_size: number
  file_type?: string
  created_at: string
}

interface Subject {
  id: number
  code: string
  label_fr: string
  label_en: string
  default_coefficient: number
  school_id?: number
  academic_year_id?: number
  section_id?: number
  specialty_id?: number
  section_name?: string
  specialty_name?: string
}

interface ClassItem {
  id: number
  name: string
  abbreviation: string
  level_id: number
  level_name?: string
  section_id?: number
  section_name?: string
  school_id?: number
  academic_year_id?: number
}

interface SubjectCoefficient {
  id: number
  subject_id: number
  class_id: number
  coefficient: number
  class_name?: string
  class_level?: string
}

interface AssignmentForm {
  teacher_id: number
  subject_id: number
  class_id: number
  hours_per_week: number
  coefficient: number
  is_principal_teacher: boolean
  academic_year_id: number
  school_id: number
}

interface School {
  id: number
  name: string
  acronym: string
  status: string
}

interface ExcelTeacherRow {
  first_name: string
  last_name: string
  email: string
  phone: string
  matricule: string
  qualifications: string
  subjects_specialization: string
  hire_date: string
  contract_type: string
  status: string
  password: string
}

// ==================== CONFIGS ====================
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any; bgColor: string; borderColor: string }> = {
  active: { label: 'Actif', color: 'text-green-700', icon: CheckCircle, bgColor: 'bg-green-50', borderColor: 'border-green-200' },
  inactive: { label: 'Inactif', color: 'text-gray-600', icon: XCircle, bgColor: 'bg-gray-50', borderColor: 'border-gray-200' },
  suspendu: { label: 'Suspendu', color: 'text-red-700', icon: XCircle, bgColor: 'bg-red-50', borderColor: 'border-red-200' },
  retraite: { label: 'Retraité', color: 'text-amber-700', icon: Award, bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
}

const CONTRACT_TYPES = [
  { value: 'permanent', label: 'Permanent' },
  { value: 'vacataire', label: 'Vacataire' },
  { value: 'fonctionnaire', label: 'Fonctionnaire' },
]

const MARITAL_STATUS_OPTIONS = [
  { value: 'Célibataire', label: 'Célibataire' },
  { value: 'Marié(e)', label: 'Marié(e)' },
  { value: 'Divorcé(e)', label: 'Divorcé(e)' },
  { value: 'Séparé(e)', label: 'Séparé(e)' },
  { value: 'Union libre', label: 'Union libre' },
  { value: 'Veuf(ve)', label: 'Veuf(ve)' },
]

const GENDER_OPTIONS = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' },
]

const TIME_SLOTS = [
  '07:00-07:59', '08:00-08:59', '09:00-09:59', '10:00-10:59',
  '11:00-11:59', '12:00-12:59', '13:00-13:59', '14:00-14:59',
  '15:00-15:59', '16:00-16:59', '17:00-17:59'
]

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const AVAILABILITY_DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi']

const SORT_OPTIONS = [
  { value: 'name', label: { fr: 'Nom', en: 'Name' } },
  { value: 'subject', label: { fr: 'Matière', en: 'Subject' } },
  { value: 'class', label: { fr: 'Classe', en: 'Class' } },
  { value: 'level', label: { fr: 'Niveau', en: 'Level' } },
  { value: 'section', label: { fr: 'Section', en: 'Section' } },
  { value: 'contract', label: { fr: 'Contrat', en: 'Contract' } },
  { value: 'status', label: { fr: 'Statut', en: 'Status' } },
]

const EXCEL_TEMPLATE_HEADERS = [
  'first_name', 'last_name', 'email', 'phone', 'matricule',
  'qualifications', 'subjects_specialization', 'hire_date',
  'contract_type', 'status', 'password'
]

const EXCEL_TEMPLATE_SAMPLE = [
  ['Jean', 'Dupont', 'jean.dupont@lycee.cm', '670123456', 'ENS-2025-001',
   'Master Math;CAPES', 'Mathématiques;Physique', '2025-01-15',
   'permanent', 'active', 'MotDePasse123'],
  ['Marie', 'Martin', 'marie.martin@lycee.cm', '670987654', 'ENS-2025-002',
   'Doctorat Chimie', 'Chimie;Biologie', '2025-02-01',
   'vacataire', 'active', 'MotDePasse456'],
]

const REQUIRED_IMPORT_FIELDS = ['first_name', 'last_name', 'email', 'matricule', 'password']

const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

export default function TeachersPage() {
  const { lang } = useLang()
  const { user, hasPermission, isAdmin, isSuperAdmin, roleName } = useAuthStore()

  const canWrite = useMemo(() => {
    return isSuperAdmin || isAdmin || hasPermission('teachers.manage') || hasPermission('subjects.manage')
  }, [isSuperAdmin, isAdmin, hasPermission])

  const canImportExcel = useMemo(() => {
    return isSuperAdmin || isAdmin || roleName === 'proviseur' || roleName === 'censeur' || hasPermission('teachers.import')
  }, [isSuperAdmin, isAdmin, roleName, hasPermission])

  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number>(() => {
    const stored = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    return stored ? parseInt(stored) : 1
  })
  const [selectedAcademicYearLabel, setSelectedAcademicYearLabel] = useState<string>('')

  const [filters, setFilters] = useState({ 
    status: '', contract_type: '', search: '', subject: '', class: '', level: '', section: '', school_id: '' 
  })
  const [showFilters, setShowFilters] = useState(false)

  const [sortBy, setSortBy] = useState<string>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const sortDropdownRef = useRef<HTMLDivElement>(null)

  const [showExportMenu, setShowExportMenu] = useState(false)
  const exportMenuRef = useRef<HTMLDivElement>(null)

  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [showAssign, setShowAssign] = useState(false)
  const [showCoeffModal, setShowCoeffModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false)
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [teacherToDelete, setTeacherToDelete] = useState<Teacher | null>(null)
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null)
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null)
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null)

  const [showImportModal, setShowImportModal] = useState(false)
  const [importPreview, setImportPreview] = useState<ExcelTeacherRow[]>([])
  const [importLoading, setImportLoading] = useState(false)
  const [importErrors, setImportErrors] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', matricule: '',
    date_of_birth: '', place_of_birth: '', gender: '', nationality: 'Camerounaise',
    profession: '', diploma: '', specialty: '',
    neighborhood: '', city: '', address: '',
    marital_status: '', number_of_children: 0,
    qualifications: '', subjects_specialization: [] as string[],
    hire_date: new Date().toISOString().split('T')[0],
    contract_type: 'permanent', status: 'active', photo_url: '',
  })

  const [availabilityData, setAvailabilityData] = useState<Record<string, boolean>>({})
  const [uploadingFile, setUploadingFile] = useState(false)
  const [showSpecDropdown, setShowSpecDropdown] = useState(false)
  const specDropdownRef = useRef<HTMLDivElement>(null)

  const [assignmentForm, setAssignmentForm] = useState<Partial<AssignmentForm>>({
    subject_id: 0, class_id: 0, hours_per_week: 2, coefficient: 1, is_principal_teacher: false,
    academic_year_id: 1, school_id: 1,
  })

  const [subjectCoefficients, setSubjectCoefficients] = useState<SubjectCoefficient[]>([])
  const [newCoeff, setNewCoeff] = useState({ class_id: 0, coefficient: 1 })

  // ==================== LOAD DATA ====================
  const loadTeachers = useCallback(async (yearId: number = selectedAcademicYearId) => {
    setLoading(true)
    try {
      const params: Record<string, string> = {
        academic_year_id: yearId.toString(),
        school_id: (user?.school_id || 1).toString(),
      }
      if (filters.status) params.status = filters.status
      if (filters.contract_type) params.contract_type = filters.contract_type
      if (filters.search) params.search = filters.search
      if (filters.school_id && isSuperAdmin) params.school_id = filters.school_id

      const response = await teachersApi.getAll(params)
      const items = Array.isArray(response) ? response : ((response as any).items || [])
      const parsedItems = items.map((teacher: any) => {
        let tsc = teacher.teacher_subject_classes
        if (tsc === null || tsc === undefined) { tsc = [] }
        else if (typeof tsc === 'string') { try { tsc = JSON.parse(tsc) } catch { tsc = [] } }
        else if (!Array.isArray(tsc)) { tsc = [tsc] }
        if (!Array.isArray(tsc)) tsc = []
        return { ...teacher, teacher_subject_classes: tsc }
      })
      setTeachers(parsedItems)
    } catch (err) {
      toast.error(lang === 'fr' ? 'Erreur chargement enseignants' : 'Error loading teachers')
    } finally { setLoading(false) }
  }, [filters, lang, user, selectedAcademicYearId, isSuperAdmin])

  const loadSubjects = useCallback(async (yearId: number = selectedAcademicYearId) => {
    try {
      let res: any, items: any[] = []
      try {
        res = await apiService.get(`/subjects?school_id=${user?.school_id || 1}&academic_year_id=${yearId}`)
        items = Array.isArray(res) ? res : (res?.items || res?.data || [])
      } catch (err: any) {
        if (err?.response?.status === 422 || err?.response?.status === 404) {
          res = await apiService.get(`/subjects?school_id=${user?.school_id || 1}`)
          items = Array.isArray(res) ? res : (res?.items || res?.data || [])
        } else { throw err }
      }
      if (items.length === 0) {
        try { res = await apiService.get('/subjects'); items = Array.isArray(res) ? res : (res?.items || res?.data || []) } catch (e) {}
      }
      const activeItems = items.filter((s: any) => s.status === 'ACTIVE' || s.status === 'active' || !s.status)
      setSubjects(activeItems)
    } catch (e: any) { setSubjects([]) }
  }, [user, selectedAcademicYearId])

  const loadClasses = useCallback(async (yearId: number = selectedAcademicYearId) => {
    try {
      const res = await classesApi.getAll({ academic_year_id: yearId, school_id: user?.school_id || 1 })
      const items = Array.isArray(res) ? res : (res.items || res.data || [])
      setClasses(items)
    } catch (e) { console.warn('Classes load error', e) }
  }, [user, selectedAcademicYearId])

  const loadSchools = useCallback(async () => {
    if (!isSuperAdmin) return
    try {
      const res: any = await apiService.get('/schools')
      const items = Array.isArray(res) ? res : (res?.items || res?.data || [])
      setSchools(items)
    } catch (e) { setSchools([]) }
  }, [isSuperAdmin])

  const loadCorrectAcademicYear = useCallback(async () => {
    try {
      const schoolId = user?.school_id || 1
      const res: any = await apiService.get(`/schools/${schoolId}/academic-years/selected`)
      if (res && res.id) {
        setSelectedAcademicYearId(res.id)
        setSelectedAcademicYearLabel(res.label || `Année ${res.id}`)
        localStorage.setItem(SELECTED_YEAR_ID_KEY, res.id.toString())
        return res.id
      }
    } catch (e: any) {}
    return selectedAcademicYearId
  }, [user, selectedAcademicYearId])

  const loadStats = useCallback(() => {
    const items = teachers
    setStats({
      total_teachers: items.length,
      active_teachers: items.filter((t) => t.status === 'active').length,
      by_contract_type: items.reduce((acc: Record<string, number>, t) => { acc[t.contract_type] = (acc[t.contract_type] || 0) + 1; return acc }, {}),
      by_status: items.reduce((acc: Record<string, number>, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc }, {}),
      new_this_year: items.filter((t) => { if (!t.hire_date) return false; return new Date(t.hire_date).getFullYear() === new Date().getFullYear() }).length,
    })
  }, [teachers])

  useEffect(() => {
    loadCorrectAcademicYear().then((yearId) => {
      loadTeachers(yearId); loadSubjects(yearId); loadClasses(yearId); loadSchools()
    })
  }, [])

  useEffect(() => { loadStats() }, [loadStats])

  useEffect(() => {
    const handleYearChange = (e: any) => {
      const detail = e.detail
      if (detail?.year_id) {
        const newYearId = detail.year_id
        setSelectedAcademicYearId(newYearId)
        localStorage.setItem(SELECTED_YEAR_ID_KEY, newYearId.toString())
        if (detail.label) setSelectedAcademicYearLabel(detail.label)
        loadTeachers(newYearId); loadSubjects(newYearId); loadClasses(newYearId)
      }
    }
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)
    return () => window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (specDropdownRef.current && !specDropdownRef.current.contains(event.target as Node)) setShowSpecDropdown(false)
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) setShowSortDropdown(false)
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) setShowExportMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ==================== SORT & FILTER ====================
  const sortedTeachers = useMemo(() => {
    let result = [...teachers]
    if (filters.subject) {
      result = result.filter(t => t.teacher_subject_classes?.some((tsc: any) => 
        tsc.subject_name?.toLowerCase().includes(filters.subject.toLowerCase()) ||
        tsc.subject?.label_fr?.toLowerCase().includes(filters.subject.toLowerCase())
      ))
    }
    if (filters.class) {
      result = result.filter(t => t.teacher_subject_classes?.some((tsc: any) => 
        tsc.class_name?.toLowerCase().includes(filters.class.toLowerCase()) ||
        tsc.class_?.name?.toLowerCase().includes(filters.class.toLowerCase())
      ))
    }
    if (filters.level) {
      result = result.filter(t => t.teacher_subject_classes?.some((tsc: any) => 
        tsc.level_name?.toLowerCase().includes(filters.level.toLowerCase()) ||
        tsc.class_?.level_name?.toLowerCase().includes(filters.level.toLowerCase())
      ))
    }
    if (filters.section) {
      result = result.filter(t => t.teacher_subject_classes?.some((tsc: any) => 
        tsc.section_name?.toLowerCase().includes(filters.section.toLowerCase())
      ))
    }
    if (filters.school_id && isSuperAdmin) {
      result = result.filter(t => t.school_id === parseInt(filters.school_id))
    }
    result.sort((a, b) => {
      let comparison = 0
      switch (sortBy) {
        case 'name':
          comparison = (a.last_name || a.user?.last_name || '').localeCompare(b.last_name || b.user?.last_name || '')
          break
        case 'subject':
          const aSubject = a.teacher_subject_classes?.[0]?.subject_name || a.teacher_subject_classes?.[0]?.subject?.label_fr || ''
          const bSubject = b.teacher_subject_classes?.[0]?.subject_name || b.teacher_subject_classes?.[0]?.subject?.label_fr || ''
          comparison = aSubject.localeCompare(bSubject)
          break
        case 'class':
          const aClass = a.teacher_subject_classes?.[0]?.class_name || a.teacher_subject_classes?.[0]?.class_?.name || ''
          const bClass = b.teacher_subject_classes?.[0]?.class_name || b.teacher_subject_classes?.[0]?.class_?.name || ''
          comparison = aClass.localeCompare(bClass)
          break
        case 'level':
          const aLevel = a.teacher_subject_classes?.[0]?.level_name || a.teacher_subject_classes?.[0]?.class_?.level_name || ''
          const bLevel = b.teacher_subject_classes?.[0]?.level_name || b.teacher_subject_classes?.[0]?.class_?.level_name || ''
          comparison = aLevel.localeCompare(bLevel)
          break
        case 'section':
          const aSection = a.teacher_subject_classes?.[0]?.section_name || ''
          const bSection = b.teacher_subject_classes?.[0]?.section_name || ''
          comparison = aSection.localeCompare(bSection)
          break
        case 'contract':
          comparison = (a.contract_type || '').localeCompare(b.contract_type || '')
          break
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '')
          break
      }
      return sortDir === 'asc' ? comparison : -comparison
    })
    return result
  }, [teachers, sortBy, sortDir, filters, isSuperAdmin])

  // ==================== EXPORT ====================
  const exportToCSV = () => {
    const headers = ['N°', 'Matricule', 'Nom', 'Prénom', 'Email', 'Téléphone', 'Qualifications', 'Spécialisations', 'Matières', 'Classes', 'Heures/semaine', 'Contrat', 'Statut', 'Date embauche']
    const rows = sortedTeachers.map((t, i) => [
      (i + 1).toString(), t.matricule,
      t.last_name || t.user?.last_name || '', t.first_name || t.user?.first_name || '',
      t.email || t.user?.email || '', t.phone || t.user?.phone || '',
      (t.qualifications || []).join('; '), (t.subjects_specialization || []).join('; '),
      (t.teacher_subject_classes || []).map((tsc: any) => tsc.subject_name || tsc.subject?.label_fr || '').join('; '),
      (t.teacher_subject_classes || []).map((tsc: any) => tsc.class_name || tsc.class_?.name || '').join('; '),
      (t.teacher_subject_classes || []).reduce((sum: number, tsc: any) => sum + (tsc.hours_per_week || 0), 0).toString(),
      CONTRACT_TYPES.find(ct => ct.value === t.contract_type)?.label || t.contract_type,
      t.status || '', t.hire_date || '',
    ])
    const csv = [headers, ...rows].map(row => row.map(cell => `\"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `enseignants_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    toast.success(lang === 'fr' ? 'Export CSV réussi' : 'CSV export successful')
  }

  const exportToPDF = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) { toast.error(lang === 'fr' ? 'Veuillez autoriser les popups' : 'Please allow popups'); return }
    const html = `<!DOCTYPE html><html><head><title>${lang === 'fr' ? 'Liste des Enseignants' : 'Teacher List'}</title><style>@page { size: landscape; margin: 15mm; } body { font-family: 'Segoe UI', Arial, sans-serif; margin: 20px; color: #333; } .header { text-align: center; margin-bottom: 25px; padding-bottom: 15px; border-bottom: 3px solid #1e40af; } .header h1 { color: #1e40af; margin: 0 0 5px 0; font-size: 22px; } .header h2 { color: #64748b; margin: 0; font-size: 16px; font-weight: 400; } .meta { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 11px; color: #666; } table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 10px; } th { background: linear-gradient(135deg, #1e40af, #3b82f6); color: white; padding: 8px 6px; text-align: left; font-weight: 600; border: 1px solid #1e40af; } td { padding: 6px; border: 1px solid #e2e8f0; text-align: left; vertical-align: top; } tr:nth-child(even) { background-color: #f8fafc; } .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 9px; font-weight: 600; } .badge-green { background: #dcfce7; color: #166534; } .badge-gray { background: #f1f5f9; color: #475569; } .badge-red { background: #fee2e2; color: #991b1b; } .badge-amber { background: #fef3c7; color: #92400e; } .footer { margin-top: 20px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }</style></head><body><div class="header"><h1>LYCÉE BILINGUE DE BALENG</h1><h2>${lang === 'fr' ? 'Liste des Enseignants' : 'Teacher List'} — ${selectedAcademicYearLabel || ''}</h2></div><div class="meta"><span>${lang === 'fr' ? 'Total' : 'Total'}: ${sortedTeachers.length} ${lang === 'fr' ? 'enseignants' : 'teachers'}</span><span>${new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span></div><table><thead><tr><th style="width:3%">N°</th><th style="width:8%">${lang === 'fr' ? 'Matricule' : 'Reg. No.'}</th><th style="width:12%">${lang === 'fr' ? 'Nom' : 'Name'}</th><th style="width:10%">${lang === 'fr' ? 'Prénom' : 'First'}</th><th style="width:14%">Email</th><th style="width:8%">${lang === 'fr' ? 'Téléphone' : 'Phone'}</th><th style="width:15%">${lang === 'fr' ? 'Matières' : 'Subjects'}</th><th style="width:8%">${lang === 'fr' ? 'Contrat' : 'Contract'}</th><th style="width:6%">${lang === 'fr' ? 'Statut' : 'Status'}</th></tr></thead><tbody>${sortedTeachers.map((t, i) => { const statusClass = t.status === 'active' ? 'badge-green' : t.status === 'inactive' ? 'badge-gray' : t.status === 'suspendu' ? 'badge-red' : 'badge-amber'; const statusLabel = t.status === 'active' ? 'Actif' : t.status === 'inactive' ? 'Inactif' : t.status === 'suspendu' ? 'Suspendu' : 'Retraité'; return `<tr><td>${i + 1}</td><td><strong>${t.matricule}</strong></td><td>${t.last_name || t.user?.last_name || ''}</td><td>${t.first_name || t.user?.first_name || ''}</td><td>${t.email || t.user?.email || ''}</td><td>${t.phone || t.user?.phone || '-'}</td><td>${(t.teacher_subject_classes || []).map((tsc: any) => tsc.subject_name || tsc.subject?.label_fr || '').join(', ')}</td><td>${CONTRACT_TYPES.find(ct => ct.value === t.contract_type)?.label || t.contract_type}</td><td><span class="badge ${statusClass}">${statusLabel}</span></td></tr>` }).join('')}</tbody></table><div class="footer">${lang === 'fr' ? 'Document généré automatiquement par le système de gestion scolaire' : 'Document automatically generated by the school management system'}</div></body></html>`
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.print()
    toast.success(lang === 'fr' ? 'Export PDF lancé' : 'PDF export started')
  }

  // ==================== IMPORT ====================
  const downloadExcelTemplate = async () => {
    try {
      const XLSX = await import('xlsx')
      const wsData = XLSX.utils.aoa_to_sheet([EXCEL_TEMPLATE_HEADERS, ...EXCEL_TEMPLATE_SAMPLE])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, wsData, 'Enseignants')
      XLSX.writeFile(wb, `modele_enseignants_${new Date().toISOString().split('T')[0]}.xlsx`)
      toast.success(lang === 'fr' ? 'Modèle Excel téléchargé' : 'Excel template downloaded')
    } catch (err) {
      const csv = [EXCEL_TEMPLATE_HEADERS.join(','), ...EXCEL_TEMPLATE_SAMPLE.map(row => row.map(cell => `\"${cell}"`).join(','))].join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `modele_enseignants_${new Date().toISOString().split('T')[0]}.csv`
      link.click()
    }
  }

  /** Parse un fichier .xlsx / .xls via SheetJS */
/** Normalise une cellule Excel en chaîne :
 *  - nombres (téléphone, matricule) → String()
 *  - dates → 'AAAA-MM-JJ' (Pydantic exige une date exacte, pas un datetime)
 *  - null/undefined → '' */
const normalizeCell = (v: any): string => {
  if (v === null || v === undefined) return ''
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return ''
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`
  }
  return String(v)
}

const parseXLSXFile = async (file: File): Promise<ExcelTeacherRow[]> => {
  const XLSX = await import('xlsx')
  const buffer = await file.arrayBuffer()
  // cellDates: true → les dates Excel deviennent des objets Date (sinon numéros de série)
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true })
  const wsName = wb.SheetNames[0]
  if (!wsName) throw new Error(lang === 'fr' ? 'Fichier Excel vide' : 'Empty Excel file')
  const ws = wb.Sheets[wsName]
  // defval: '' garantit que chaque colonne existe même si une cellule est vide
  const rows = XLSX.utils.sheet_to_json<any>(ws, { defval: '' })
  if (!rows || rows.length === 0) {
    throw new Error(lang === 'fr' ? 'Fichier vide ou invalide' : 'Empty or invalid file')
  }
  // Normaliser les en-têtes (trim + lowercase) ET les valeurs (tout en string)
  const normalized = rows.map((r: any) => {
    const out: any = {}
    Object.keys(r).forEach((k) => { out[k.trim().toLowerCase()] = normalizeCell(r[k]).trim() })
    return out
  })
  const headers = Object.keys(normalized[0] || {})
  const missingFields = REQUIRED_IMPORT_FIELDS.filter((f) => !headers.includes(f))
  if (missingFields.length > 0) {
    throw new Error(
      (lang === 'fr' ? 'Colonnes manquantes: ' : 'Missing columns: ') + missingFields.join(', ') +
      (lang === 'fr'
        ? '. En-têtes attendus: ' + EXCEL_TEMPLATE_HEADERS.join(', ')
        : '. Expected headers: ' + EXCEL_TEMPLATE_HEADERS.join(', '))
    )
  }
  return normalized.filter((r: any) => r.first_name && r.last_name && r.email)
}

/** Parse un fichier .csv */
const parseCSVFile = (file: File): Promise<ExcelTeacherRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const lines = text.split('\n').filter((line) => line.trim())
        if (lines.length < 2) { reject(new Error(lang === 'fr' ? 'Fichier vide ou invalide' : 'Empty or invalid file')); return }
        const headerLine = lines[0].replace(/^\uFEFF/, '').trim()
        const headers = headerLine.split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase())
        const missingFields = REQUIRED_IMPORT_FIELDS.filter((f) => !headers.includes(f))
        if (missingFields.length > 0) {
          reject(new Error(
            (lang === 'fr' ? 'Colonnes manquantes: ' : 'Missing columns: ') + missingFields.join(', ')
          )); return
        }
        const rows: ExcelTeacherRow[] = []
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim()
          if (!line) continue
          const values: string[] = []
          let current = '', inQuotes = false
          for (let j = 0; j < line.length; j++) {
            const char = line[j]
            if (char === '"') { if (inQuotes && line[j + 1] === '"') { current += '"'; j++ } else { inQuotes = !inQuotes } }
            else if (char === ',' && !inQuotes) { values.push(current.trim()); current = '' } else { current += char }
          }
          values.push(current.trim())
          const row: any = {}
          headers.forEach((h, idx) => { row[h] = values[idx] || '' })
          if (row.first_name && row.last_name && row.email) rows.push(row as ExcelTeacherRow)
        }
        resolve(rows)
      } catch (err) { reject(err) }
    }
    reader.onerror = () => reject(new Error(lang === 'fr' ? 'Erreur lecture fichier' : 'File read error'))
    reader.readAsText(file)
  })
}

/** Point d'entrée unique : route vers le bon parseur selon l'extension */
const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0]
  if (!file) return
  setImportLoading(true)
  setImportErrors([])
  try {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    let rows: ExcelTeacherRow[] = []
    if (ext === 'xlsx' || ext === 'xls') {
      rows = await parseXLSXFile(file)
    } else if (ext === 'csv' || ext === 'txt') {
      rows = await parseCSVFile(file)
    } else {
      toast.error(lang === 'fr' ? 'Format non supporté (CSV ou Excel uniquement)' : 'Unsupported format (CSV or Excel only)')
      return
    }
    if (rows.length === 0) { toast.error(lang === 'fr' ? 'Aucune donnée valide trouvée' : 'No valid data found'); return }
    const errors: string[] = []
    rows.forEach((row, idx) => {
      if (!String(row.email).includes('@')) errors.push(`Ligne ${idx + 2}: email invalide "${row.email}"`)
      if (!row.matricule) errors.push(`Ligne ${idx + 2}: matricule manquant`)
      if (!row.password || String(row.password).length < 6) errors.push(`Ligne ${idx + 2}: mot de passe trop court`)
      if (!['permanent', 'vacataire', 'fonctionnaire'].includes(row.contract_type)) errors.push(`Ligne ${idx + 2}: type contrat invalide`)
      if (!['active', 'inactive', 'suspendu', 'retraite'].includes(row.status)) errors.push(`Ligne ${idx + 2}: statut invalide`)
    })
    setImportErrors(errors)
    setImportPreview(rows)
    setShowImportModal(true)
    toast.success(`${rows.length} ${lang === 'fr' ? 'enseignants trouvés' : 'teachers found'}`)
  } catch (err: any) {
    toast.error(err.message || (lang === 'fr' ? 'Erreur lors du parsing' : 'Parsing error'))
  } finally {
    setImportLoading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }
}

const handleImportConfirm = async () => {
    if (importErrors.length > 0) {
      const confirmMsg = lang === 'fr' ? `${importErrors.length} erreurs trouvées. Continuer quand même ?` : `${importErrors.length} errors found. Continue anyway?`
      if (!confirm(confirmMsg)) return
    }
    setImportLoading(true)
    let successCount = 0, errorCount = 0
    const rowErrors: string[] = []
    for (const row of importPreview) {
      try {
        await teachersApi.create({
          first_name: String(row.first_name || '').trim(),
          last_name: String(row.last_name || '').trim(),
          email: String(row.email || '').trim(),
          phone: row.phone ? String(row.phone).trim() : null,
          matricule: String(row.matricule || '').trim(),
          qualifications: row.qualifications ? String(row.qualifications).split(/[;,]/).map((q: string) => q.trim()).filter((q: string) => q.length > 0) : null,
          subjects_specialization: row.subjects_specialization ? String(row.subjects_specialization).split(/[;,]/).map((s: string) => s.trim()).filter((s: string) => s.length > 0) : null,
          hire_date: row.hire_date ? String(row.hire_date).trim().slice(0, 10) : new Date().toISOString().split('T')[0],
          contract_type: row.contract_type || 'permanent', status: row.status || 'active',
          password: String(row.password || ''), school_id: user?.school_id || 1, academic_year_id: selectedAcademicYearId,
        })
        successCount++
      } catch (err: any) {
        errorCount++
        // Capturer le détail exact de l'erreur serveur (422, 400, 409...)
        const serverDetail = err?.response?.data?.detail
        const serverErrors = err?.response?.data?.errors
        let msg = typeof serverDetail === 'string' ? serverDetail : JSON.stringify(serverDetail)
        if (Array.isArray(serverErrors) && serverErrors.length > 0) {
          msg = serverErrors.map((e: any) => `${e.field}: ${e.message}`).join(' | ')
        }
        rowErrors.push(`Ligne ${importPreview.indexOf(row) + 2} (${row.last_name} ${row.first_name}): ${msg || err.message}`)
      }
    }
    setImportLoading(false)
    if (errorCount === 0) {
      setShowImportModal(false); setImportPreview([]); setImportErrors([])
    }
    if (successCount > 0) { toast.success(`${successCount} ${lang === 'fr' ? 'enseignants créés' : 'teachers created'}${errorCount > 0 ? `, ${errorCount} erreur(s)` : ''}`); loadTeachers() }
    else if (rowErrors.length > 0) {
      // Afficher la 1ère erreur serveur en entier + toutes dans la console
      console.error('[Import enseignants] Erreurs serveur:', rowErrors)
      toast.error(rowErrors[0], { duration: 8000 })
    }
    else { toast.error(lang === 'fr' ? "Échec de l'import" : 'Import failed') }
  }

// ==================== CRUD HANDLERS ====================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const parsedQualifications = formData.qualifications.split(/[;,]/).map(q => q.trim()).filter(q => q.length > 0)
      const data: any = {
        first_name: formData.first_name, last_name: formData.last_name, email: formData.email,
        phone: formData.phone || null, matricule: formData.matricule,
        date_of_birth: formData.date_of_birth || null, place_of_birth: formData.place_of_birth || null,
        gender: formData.gender || null, nationality: formData.nationality || null,
        profession: formData.profession || null, diploma: formData.diploma || null, specialty: formData.specialty || null,
        neighborhood: formData.neighborhood || null, city: formData.city || null, address: formData.address || null,
        marital_status: formData.marital_status || null, number_of_children: formData.number_of_children || null,
        qualifications: parsedQualifications.length > 0 ? parsedQualifications : null,
        subjects_specialization: formData.subjects_specialization.length > 0 ? formData.subjects_specialization : null,
        hire_date: formData.hire_date || null, contract_type: formData.contract_type, status: formData.status,
        school_id: user?.school_id || 1, academic_year_id: selectedAcademicYearId, photo_url: formData.photo_url || null,
      }
      if (!editingTeacher) data.password = formData.password
      else if (formData.password) data.password = formData.password

      if (editingTeacher) {
        await teachersApi.update(editingTeacher.id, data)
        toast.success(lang === 'fr' ? 'Enseignant mis à jour' : 'Teacher updated')
      } else {
        await teachersApi.create(data)
        toast.success(lang === 'fr' ? 'Enseignant créé avec compte utilisateur' : 'Teacher created with user account')
      }
      setShowForm(false); setEditingTeacher(null); resetForm(); loadTeachers()
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message
      toast.error(msg || (lang === 'fr' ? "Erreur lors de l'enregistrement" : 'Error during registration'))
    }
  }

  const handleToggleStatus = async (teacher: Teacher) => {
    if (!canWrite) { toast.error(lang === 'fr' ? 'Permission refusée' : 'Permission denied'); return }
    const newStatus = teacher.status === 'active' ? 'inactive' : 'active'
    try {
      await teachersApi.update(teacher.id, {
        first_name: teacher.first_name || teacher.user?.first_name || '',
        last_name: teacher.last_name || teacher.user?.last_name || '',
        email: teacher.email || teacher.user?.email || '',
        phone: teacher.phone || teacher.user?.phone || null,
        matricule: teacher.matricule, qualifications: teacher.qualifications || null,
        subjects_specialization: teacher.subjects_specialization || null,
        hire_date: teacher.hire_date || null, contract_type: teacher.contract_type || 'permanent', status: newStatus,
        school_id: teacher.school_id || user?.school_id || 1, academic_year_id: teacher.academic_year_id || selectedAcademicYearId,
        date_of_birth: teacher.date_of_birth || null, place_of_birth: teacher.place_of_birth || null,
        gender: teacher.gender || null, nationality: teacher.nationality || null,
        profession: teacher.profession || null, diploma: teacher.diploma || null, specialty: teacher.specialty || null,
        neighborhood: teacher.neighborhood || null, city: teacher.city || null, address: teacher.address || null,
        marital_status: teacher.marital_status || null, number_of_children: teacher.number_of_children || null,
        photo_url: teacher.photo_url || null,
      })
      toast.success(lang === 'fr' ? `Enseignant ${newStatus === 'active' ? 'activé' : 'désactivé'}` : `Teacher ${newStatus === 'active' ? 'activated' : 'deactivated'}`)
      loadTeachers()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur lors du changement de statut' : 'Status change error'))
    }
  }

  const openDeleteConfirm = (teacher: Teacher) => {
    if (!canWrite) { toast.error(lang === 'fr' ? 'Permission refusée' : 'Permission denied'); return }
    setTeacherToDelete(teacher); setShowDeleteConfirm(true)
  }

  const handleDelete = async () => {
    if (!teacherToDelete) return
    try { await teachersApi.delete(teacherToDelete.id); toast.success(lang === 'fr' ? 'Enseignant supprimé' : 'Teacher deleted'); setShowDeleteConfirm(false); setTeacherToDelete(null); loadTeachers() }
    catch (err) { toast.error(lang === 'fr' ? 'Erreur lors de la suppression' : 'Delete error') }
  }

  const handleAssignSubject = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canWrite) { toast.error(lang === 'fr' ? 'Permission refusée' : 'Permission denied'); return }
    if (!selectedTeacher || !assignmentForm.subject_id || !assignmentForm.class_id) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner une matière et une classe' : 'Please select a subject and a class'); return
    }
    try {
      await teachersApi.createAssignment({
        teacher_id: selectedTeacher.id, subject_id: assignmentForm.subject_id as number,
        class_id: assignmentForm.class_id as number, hours_per_week: assignmentForm.hours_per_week || 2,
        coefficient: assignmentForm.coefficient || 1, is_principal_teacher: assignmentForm.is_principal_teacher || false,
        academic_year_id: selectedAcademicYearId, school_id: user?.school_id || 1,
      })
      toast.success(lang === 'fr' ? 'Matière assignée avec succès' : 'Subject assigned successfully')
      setShowAssign(false); loadTeachers()
    } catch (err: any) { toast.error(err.response?.data?.detail || (lang === 'fr' ? "Erreur d'assignation" : 'Assignment error')) }
  }

  // ==================== AVAILABILITY ====================
  const loadTeacherAvailabilities = async (teacherId: number) => {
    try {
      const res: any = await apiService.get(`/teachers/${teacherId}/availabilities`)
      const items = Array.isArray(res) ? res : (res?.items || res?.data || [])
      const availabilityMap: Record<string, boolean> = {}
      items.forEach((av: TeacherAvailability) => { availabilityMap[`${av.day_of_week}-${av.time_slot}`] = av.is_available })
      setAvailabilityData(availabilityMap)
    } catch (e) { setAvailabilityData({}) }
  }

  const saveAvailabilities = async () => {
    if (!selectedTeacher) return

    // Générer toutes les combinaisons jours (1-5) × créneaux avec true par défaut
    const allAvailabilities: { teacher_id: number; day_of_week: number; time_slot: string; is_available: boolean }[] = []
    let availableCount = 0

    for (let day = 1; day <= 5; day++) {
      for (const timeSlot of TIME_SLOTS) {
        const key = `${day}-${timeSlot}`
        // Par défaut disponible (true), sauf si explicitement décoché (false)
        const is_available = availabilityData[key] !== false
        if (is_available) availableCount++
        allAvailabilities.push({
          teacher_id: selectedTeacher.id,
          day_of_week: day,
          time_slot: timeSlot,
          is_available
        })
      }
    }

    // Validation: compter les disponibilités (min 2, max 30)
    if (availableCount < 2) {
      toast.error(lang === 'fr' ? `Minimum 2 créneaux disponibles requis (actuel: ${availableCount})` : `Minimum 2 available slots required (current: ${availableCount})`)
      return
    }
    if (availableCount > 30) {
      toast.error(lang === 'fr' ? `Maximum 30 créneaux disponibles autorisés (actuel: ${availableCount})` : `Maximum 30 available slots allowed (current: ${availableCount})`)
      return
    }

    try {
      await apiService.post(`/teachers/${selectedTeacher.id}/availabilities`, allAvailabilities)
      toast.success(lang === 'fr' ? 'Disponibilités enregistrées' : 'Availabilities saved')
      setShowAvailabilityModal(false)
    } catch (err: any) { 
      console.error('Availability save error:', err)
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur lors de la sauvegarde' : 'Save error')) 
    }
  }

  // ==================== ATTACHMENTS ====================
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedTeacher) return
    setUploadingFile(true)
    try {
      await apiService.upload(`/teachers/${selectedTeacher.id}/attachments`, file)
      toast.success(lang === 'fr' ? 'Fichier uploadé' : 'File uploaded')
      const res: any = await apiService.get(`/teachers/${selectedTeacher.id}`)
      setSelectedTeacher(res)
    } catch (err: any) { toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur upload' : 'Upload error')) }
    finally { setUploadingFile(false) }
  }

  const deleteAttachment = async (attachmentId: number) => {
    if (!selectedTeacher) return
    if (!confirm(lang === 'fr' ? 'Supprimer ce fichier ?' : 'Delete this file?')) return
    try {
      await apiService.delete(`/teachers/${selectedTeacher.id}/attachments/${attachmentId}`)
      toast.success(lang === 'fr' ? 'Fichier supprimé' : 'File deleted')
      const res: any = await apiService.get(`/teachers/${selectedTeacher.id}`)
      setSelectedTeacher(res)
    } catch (err) { toast.error(lang === 'fr' ? 'Erreur suppression' : 'Delete error') }
  }

  // ==================== COEFFICIENTS ====================
  const loadSubjectCoefficients = async (subjectId: number) => {
    try {
      const res: any = await apiService.get(`/subjects/${subjectId}/coefficients`)
      setSubjectCoefficients(Array.isArray(res) ? res : (res?.items || res?.data || []))
    } catch (e) { setSubjectCoefficients([]) }
  }

  const handleAddCoefficient = async () => {
    if (!canWrite || !selectedSubject || !newCoeff.class_id) return
    try {
      await apiService.post('/subjects/coefficients', { subject_id: selectedSubject.id, class_id: newCoeff.class_id, coefficient: newCoeff.coefficient })
      toast.success(lang === 'fr' ? 'Coefficient ajouté' : 'Coefficient added')
      loadSubjectCoefficients(selectedSubject.id)
    } catch (err: any) { toast.error(err.message || (lang === 'fr' ? 'Erreur coefficient' : 'Coefficient error')) }
  }

  const handleDeleteCoefficient = async (coeffId: number) => {
    if (!canWrite) return
    if (!confirm(lang === 'fr' ? 'Supprimer ce coefficient ?' : 'Delete this coefficient?')) return
    try { await apiService.delete(`/subjects/coefficients/${coeffId}`); toast.success(lang === 'fr' ? 'Coefficient supprimé' : 'Coefficient deleted'); if (selectedSubject) loadSubjectCoefficients(selectedSubject.id) }
    catch (err) { toast.error(lang === 'fr' ? 'Erreur suppression' : 'Delete error') }
  }

  // ==================== HELPERS ====================
  const toggleSubjectSpecialization = (subjectLabel: string) => {
    setFormData(prev => {
      const current = prev.subjects_specialization || []
      if (current.includes(subjectLabel)) return { ...prev, subjects_specialization: current.filter(s => s !== subjectLabel) }
      else return { ...prev, subjects_specialization: [...current, subjectLabel] }
    })
  }

  const isSubjectSelected = (subjectLabel: string) => (formData.subjects_specialization || []).includes(subjectLabel)

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', password: '', matricule: '',
      date_of_birth: '', place_of_birth: '', gender: '', nationality: 'Camerounaise',
      profession: '', diploma: '', specialty: '', neighborhood: '', city: '', address: '',
      marital_status: '', number_of_children: 0, qualifications: '', subjects_specialization: [],
      hire_date: new Date().toISOString().split('T')[0], contract_type: 'permanent', status: 'active', photo_url: '',
    })
  }

  const editTeacher = (teacher: Teacher) => {
    if (!canWrite) { toast.error(lang === 'fr' ? 'Permission refusée' : 'Permission denied'); return }
    setEditingTeacher(teacher)
    setFormData({
      first_name: teacher.first_name || teacher.user?.first_name || '',
      last_name: teacher.last_name || teacher.user?.last_name || '',
      email: teacher.email || teacher.user?.email || '',
      phone: teacher.phone || teacher.user?.phone || '', password: '',
      matricule: teacher.matricule || '',
      date_of_birth: teacher.date_of_birth || '', place_of_birth: teacher.place_of_birth || '',
      gender: teacher.gender || '', nationality: teacher.nationality || 'Camerounaise',
      profession: teacher.profession || '', diploma: teacher.diploma || '', specialty: teacher.specialty || '',
      neighborhood: teacher.neighborhood || '', city: teacher.city || '', address: teacher.address || '',
      marital_status: teacher.marital_status || '', number_of_children: teacher.number_of_children || 0,
      qualifications: (teacher.qualifications || []).join('; '),
      subjects_specialization: teacher.subjects_specialization || [],
      hire_date: teacher.hire_date || new Date().toISOString().split('T')[0],
      contract_type: teacher.contract_type || 'permanent', status: teacher.status || 'active',
      photo_url: teacher.photo_url || '',
    })
    setShowForm(true)
  }

  const viewDetail = (teacher: Teacher) => { setSelectedTeacher(teacher); setShowDetail(true) }
  const openAssignModal = (teacher: Teacher) => {
    if (!canWrite) { toast.error(lang === 'fr' ? 'Permission refusée' : 'Permission denied'); return }
    setSelectedTeacher(teacher)
    setAssignmentForm({ subject_id: 0, class_id: 0, hours_per_week: 2, coefficient: 1, is_principal_teacher: false, academic_year_id: selectedAcademicYearId, school_id: user?.school_id || 1 })
    setShowAssign(true)
  }
  const openAvailabilityModal = async (teacher: Teacher) => { setSelectedTeacher(teacher); await loadTeacherAvailabilities(teacher.id); setShowAvailabilityModal(true) }
  const openAttachmentModal = (teacher: Teacher) => { setSelectedTeacher(teacher); setShowAttachmentModal(true) }
  const openCoeffModal = (subject: Subject) => { if (!canWrite) return; setSelectedSubject(subject); setNewCoeff({ class_id: 0, coefficient: subject.default_coefficient || 1 }); loadSubjectCoefficients(subject.id); setShowCoeffModal(true) }

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active
    const IconComponent = config.icon
    return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.borderColor} ${config.color}`}><IconComponent className="w-3 h-3" />{config.label}</span>
  }

  const getEffectiveCoefficient = (subject: Subject | undefined, classId: number) => {
    if (!subject) return 1
    const specific = subjectCoefficients.find((c) => c.subject_id === subject.id && c.class_id === classId)
    return specific ? specific.coefficient : (subject.default_coefficient || 1)
  }

  const clearFilters = () => setFilters({ status: '', contract_type: '', search: '', subject: '', class: '', level: '', section: '', school_id: '' })
  const handleSort = (field: string) => { if (sortBy === field) { setSortDir(prev => prev === 'asc' ? 'desc' : 'asc') } else { setSortBy(field); setSortDir('asc') } setShowSortDropdown(false) }
  const formatFileSize = (bytes: number) => { if (bytes < 1024) return bytes + ' B'; if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'; return (bytes / (1024 * 1024)).toFixed(1) + ' MB' }

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-amber-50/10">
      {/* Header */}
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-blue-100 px-6 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent">
                {lang === 'fr' ? 'Gestion des Enseignants' : 'Teacher Management'}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {lang === 'fr' ? 'Comptes utilisateurs, matières, disponibilités et coefficients' : 'User accounts, subjects, availabilities and coefficients'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">{selectedAcademicYearLabel || `Année ${selectedAcademicYearId}`}</span>
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all">
              <Filter className="w-4 h-4" />{lang === 'fr' ? 'Filtres' : 'Filters'}
            </button>
            <div ref={sortDropdownRef} className="relative">
              <button onClick={() => setShowSortDropdown(!showSortDropdown)} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all">
                <ArrowUpDown className="w-4 h-4" />{lang === 'fr' ? 'Trier' : 'Sort'}
                {sortBy !== 'name' && <span className="ml-1 text-xs text-blue-600">({SORT_OPTIONS.find(o => o.value === sortBy)?.label[lang as 'fr'|'en']})</span>}
              </button>
              {showSortDropdown && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {SORT_OPTIONS.map((opt) => (
                    <button key={opt.value} onClick={() => handleSort(opt.value)} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${sortBy === opt.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}>
                      {opt.label[lang as 'fr'|'en']}{sortBy === opt.value && (sortDir === 'asc' ? '↑' : '↓')}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div ref={exportMenuRef} className="relative">
              <button onClick={() => setShowExportMenu(!showExportMenu)} className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg text-sm font-medium hover:from-emerald-600 hover:to-teal-700 transition-all shadow-md shadow-emerald-200">
                <ArrowDownToLine className="w-4 h-4" />{lang === 'fr' ? 'Import / Export' : 'Import / Export'}
                <ChevronDown className={`w-3 h-3 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-100"><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Exporter' : 'Export'}</p></div>
                  <button onClick={() => { exportToCSV(); setShowExportMenu(false) }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-green-50 transition-colors text-left">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center"><Table2 className="w-4 h-4 text-green-600" /></div>
                    <div><p className="text-sm font-medium text-gray-800">Excel (CSV)</p><p className="text-xs text-gray-500">{lang === 'fr' ? 'Télécharger la liste' : 'Download list'}</p></div>
                  </button>
                  <button onClick={() => { exportToPDF(); setShowExportMenu(false) }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-left border-t border-gray-50">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center"><FileDown className="w-4 h-4 text-red-600" /></div>
                    <div><p className="text-sm font-medium text-gray-800">PDF</p><p className="text-xs text-gray-500">{lang === 'fr' ? 'Imprimer la liste' : 'Print list'}</p></div>
                  </button>
                  {canImportExcel && (
                    <>
                      <div className="px-4 py-2 bg-gray-50 border-y border-gray-100"><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Importer' : 'Import'}</p></div>
                      <button onClick={() => { downloadExcelTemplate(); setShowExportMenu(false) }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center"><Download className="w-4 h-4 text-purple-600" /></div>
                        <div><p className="text-sm font-medium text-gray-800">{lang === 'fr' ? 'Modèle Excel' : 'Excel Template'}</p><p className="text-xs text-gray-500">{lang === 'fr' ? 'Télécharger le modèle' : 'Download template'}</p></div>
                      </button>
                      <button onClick={() => { fileInputRef.current?.click(); setShowExportMenu(false) }} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors text-left border-t border-gray-50">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center"><Upload className="w-4 h-4 text-indigo-600" /></div>
                        <div><p className="text-sm font-medium text-gray-800">{lang === 'fr' ? 'Importer Excel' : 'Import Excel'}</p><p className="text-xs text-gray-500">{lang === 'fr' ? 'Uploader un fichier' : 'Upload a file'}</p></div>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" />
            {canWrite && (
              <button onClick={() => { setEditingTeacher(null); resetForm(); setShowForm(true) }} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md shadow-blue-200">
                <Plus className="w-4 h-4" />{lang === 'fr' ? 'Nouvel enseignant' : 'New teacher'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: GraduationCap, color: 'blue', label: lang === 'fr' ? 'Total' : 'Total', value: stats?.total_teachers || 0 },
            { icon: CheckCircle, color: 'green', label: lang === 'fr' ? 'Actifs' : 'Active', value: stats?.active_teachers || 0 },
            { icon: Users, color: 'purple', label: 'CDI', value: stats?.by_contract_type?.permanent || 0 },
            { icon: Award, color: 'amber', label: lang === 'fr' ? 'Nouveaux' : 'New', value: stats?.new_this_year || 0 },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-10 h-10 bg-${s.color}-100 rounded-lg flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 text-${s.color}-600`} />
              </div>
              <div><p className="text-2xl font-bold text-gray-900">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </div>
          ))}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {isSuperAdmin && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5"><span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{lang === 'fr' ? 'Établissement' : 'School'}</span></label>
                  <select value={filters.school_id} onChange={(e) => setFilters({ ...filters, school_id: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                    <option value="">{lang === 'fr' ? 'Tous les établissements' : 'All schools'}</option>
                    {schools.map((school) => <option key={school.id} value={school.id.toString()}>{school.acronym || school.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{lang === 'fr' ? 'Statut' : 'Status'}</label>
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Tous' : 'All'}</option>
                  {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (<option key={key} value={key}>{label}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{lang === 'fr' ? 'Type contrat' : 'Contract type'}</label>
                <select value={filters.contract_type} onChange={(e) => setFilters({ ...filters, contract_type: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Tous' : 'All'}</option>
                  {CONTRACT_TYPES.map((ct) => (<option key={ct.value} value={ct.value}>{ct.label}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{lang === 'fr' ? 'Matière' : 'Subject'}</label>
                <select value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Toutes' : 'All'}</option>
                  {subjects.map((s) => (<option key={s.id} value={s.label_fr}>{s.label_fr}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{lang === 'fr' ? 'Classe' : 'Class'}</label>
                <select value={filters.class} onChange={(e) => setFilters({ ...filters, class: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Toutes' : 'All'}</option>
                  {classes.map((c) => (<option key={c.id} value={c.name}>{c.name}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{lang === 'fr' ? 'Niveau' : 'Level'}</label>
                <select value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Tous' : 'All'}</option>
                  {Array.from(new Set(classes.map(c => c.level_name).filter(Boolean))).map((level) => (<option key={level} value={level as string}>{level}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{lang === 'fr' ? 'Section' : 'Section'}</label>
                <select value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Toutes' : 'All'}</option>
                  <option value="francophone">Francophone</option>
                  <option value="anglophone">Anglophone</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{lang === 'fr' ? 'Recherche' : 'Search'}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" placeholder={lang === 'fr' ? 'Nom, matricule...' : 'Name, registration...'} value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={clearFilters} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">{lang === 'fr' ? 'Réinitialiser' : 'Reset'}</button>
              <button onClick={() => loadTeachers()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">{lang === 'fr' ? 'Appliquer' : 'Apply'}</button>
            </div>
          </div>
        )}

        {/* Teachers Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">{lang === 'fr' ? 'Liste des enseignants' : 'Teacher list'}</h3>
            <span className="text-xs text-gray-500">{sortedTeachers.length} {lang === 'fr' ? 'entrées' : 'entries'}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Matricule' : 'Reg. No.'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Enseignant' : 'Teacher'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">{lang === 'fr' ? 'Contact' : 'Contact'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">{lang === 'fr' ? 'Matières' : 'Subjects'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Contrat' : 'Contract'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Statut' : 'Status'}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Actions' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center"><Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" /></td></tr>
                ) : sortedTeachers.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center"><GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{lang === 'fr' ? 'Aucun enseignant trouvé' : 'No teachers found'}</p></td></tr>
                ) : (
                  sortedTeachers.map((teacher) => (
                    <tr key={teacher.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3"><span className="font-mono text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{teacher.matricule}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {(teacher.first_name || teacher.user?.first_name || '')[0]}{(teacher.last_name || teacher.user?.last_name || '')[0]}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{teacher.last_name || teacher.user?.last_name} {teacher.first_name || teacher.user?.first_name}</p>
                            <p className="text-xs text-gray-500">{(teacher.subjects_specialization || []).slice(0, 2).join(', ') || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs text-gray-600"><Mail className="w-3.5 h-3.5 text-gray-400" />{teacher.email || teacher.user?.email || '-'}</div>
                          <div className="flex items-center gap-1 text-xs text-gray-600"><Phone className="w-3.5 h-3.5 text-gray-400" />{teacher.phone || teacher.user?.phone || '-'}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const tscList = teacher.teacher_subject_classes
                            if (!tscList || !Array.isArray(tscList) || tscList.length === 0) {
                              return <span className="text-xs text-gray-400 italic">{lang === 'fr' ? 'Aucune' : 'None'}</span>
                            }
                            return (
                              <>
                                {tscList.slice(0, 3).map((tsc: any) => (
                                  <span key={tsc?.id || Math.random()} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                    {tsc?.subject_name || tsc?.subject?.label_fr || 'Matière'} {tsc?.hours_per_week > 0 && `(${tsc.hours_per_week}h)`}
                                  </span>
                                ))}
                                {tscList.length > 3 && <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">+{tscList.length - 3}</span>}
                              </>
                            )
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{CONTRACT_TYPES.find(ct => ct.value === teacher.contract_type)?.label || teacher.contract_type}</span></td>
                      <td className="px-4 py-3">{getStatusBadge(teacher.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {canWrite && (
                            <button onClick={() => handleToggleStatus(teacher)} className={`p-1.5 rounded-lg transition-colors ${teacher.status === 'active' ? 'hover:bg-green-50 text-green-600' : 'hover:bg-gray-50 text-gray-400'}`} title={teacher.status === 'active' ? (lang === 'fr' ? 'Désactiver' : 'Deactivate') : (lang === 'fr' ? 'Activer' : 'Activate')}>
                              {teacher.status === 'active' ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            </button>
                          )}
                          <button onClick={() => viewDetail(teacher)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600" title="Voir"><Eye className="w-4 h-4" /></button>
                          {canWrite && (
                            <>
                              <button onClick={() => editTeacher(teacher)} className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-400 hover:text-amber-600" title="Modifier"><Edit2 className="w-4 h-4" /></button>
                              <button onClick={() => openAssignModal(teacher)} className="p-1.5 hover:bg-purple-50 rounded-lg text-gray-400 hover:text-purple-600" title="Assigner"><BookOpen className="w-4 h-4" /></button>
                              <button onClick={() => openAvailabilityModal(teacher)} className="p-1.5 hover:bg-cyan-50 rounded-lg text-gray-400 hover:text-cyan-600" title="Disponibilités"><Clock className="w-4 h-4" /></button>
                              <button onClick={() => openAttachmentModal(teacher)} className="p-1.5 hover:bg-indigo-50 rounded-lg text-gray-400 hover:text-indigo-600" title="Pièces jointes"><FileText className="w-4 h-4" /></button>
                              <button onClick={() => openDeleteConfirm(teacher)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ==================== TEACHER FORM MODAL (avec tous les nouveaux champs) ==================== */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowForm(false); setEditingTeacher(null) }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">
                {editingTeacher ? (lang === 'fr' ? "Modifier l'enseignant" : 'Edit teacher') : (lang === 'fr' ? "Nouvel enseignant" : 'New teacher')}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingTeacher(null) }} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Section 1: Identité */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><User className="w-4 h-4 text-blue-600" />{lang === 'fr' ? 'Identité' : 'Identity'}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Prénom *' : 'First name *'}</label>
                    <input required value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Nom *' : 'Last name *'}</label>
                    <input required value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Email *' : 'Email *'}</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Téléphone' : 'Phone'}</label>
                    <input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Matricule *' : 'Registration *'}</label>
                    <input required value={formData.matricule} onChange={(e) => setFormData({ ...formData, matricule: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="ENS-2025-001" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{editingTeacher ? (lang === 'fr' ? 'Mot de passe (laisser vide = inchangé)' : 'Password (empty = unchanged)') : (lang === 'fr' ? 'Mot de passe *' : 'Password *')}</label>
                    <input type="password" required={!editingTeacher} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                </div>
              </div>

              {/* Section 2: Informations personnelles */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><User className="w-4 h-4 text-green-600" />{lang === 'fr' ? 'Informations personnelles' : 'Personal Information'}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Date de naissance' : 'Date of birth'}</label>
                    <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Lieu de naissance' : 'Place of birth'}</label>
                    <input value={formData.place_of_birth} onChange={(e) => setFormData({ ...formData, place_of_birth: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Bafoussam" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Sexe' : 'Gender'}</label>
                    <select value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      {GENDER_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Nationalité' : 'Nationality'}</label>
                    <input value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                </div>
              </div>

              {/* Section 3: Informations professionnelles */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4 text-purple-600" />{lang === 'fr' ? 'Informations professionnelles' : 'Professional Information'}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Profession' : 'Profession'}</label>
                    <input value={formData.profession} onChange={(e) => setFormData({ ...formData, profession: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Enseignant" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Diplôme' : 'Diploma'}</label>
                    <input value={formData.diploma} onChange={(e) => setFormData({ ...formData, diploma: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Master en Mathématiques" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Spécialité' : 'Specialty'}</label>
                    <input value={formData.specialty} onChange={(e) => setFormData({ ...formData, specialty: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Mathématiques" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Type contrat' : 'Contract type'}</label>
                    <select value={formData.contract_type} onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                      {CONTRACT_TYPES.map((ct) => (<option key={ct.value} value={ct.value}>{ct.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? "Date d'embauche" : 'Hire date'}</label>
                    <input type="date" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Statut' : 'Status'}</label>
                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                      {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (<option key={key} value={key}>{label}</option>))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 4: Adresse */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Home className="w-4 h-4 text-orange-600" />{lang === 'fr' ? 'Adresse' : 'Address'}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Quartier' : 'Neighborhood'}</label>
                    <input value={formData.neighborhood} onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Bali" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Ville' : 'City'}</label>
                    <input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Bafoussam" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Adresse complète' : 'Full address'}</label>
                    <input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Rue 123, Bali" />
                  </div>
                </div>
              </div>

              {/* Section 5: Situation familiale */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-pink-600" />{lang === 'fr' ? 'Situation familiale' : 'Family Situation'}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Statut marital' : 'Marital status'}</label>
                    <select value={formData.marital_status} onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      {MARITAL_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? "Nombre d'enfants" : 'Number of children'}</label>
                    <input type="number" min={0} value={formData.number_of_children} onChange={(e) => setFormData({ ...formData, number_of_children: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                  </div>
                </div>
              </div>

              {/* Section 6: Qualifications & Spécialisations */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-amber-600" />{lang === 'fr' ? 'Qualifications & Spécialisations' : 'Qualifications & Specializations'}</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Qualifications' : 'Qualifications'} <span className="text-xs text-gray-400 font-normal">({lang === 'fr' ? 'séparées par ; ou ,' : 'separated by ; or ,'})</span></label>
                  <textarea value={formData.qualifications} onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })} placeholder={lang === 'fr' ? 'Ex: Master en Mathématiques; CAPES; Doctorat...' : 'Ex: Master in Mathematics; CAPES; PhD...'} rows={2} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none" />
                  {formData.qualifications && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.qualifications.split(/[;,]/).map(q => q.trim()).filter(q => q.length > 0).map((q, i) => (
                        <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium">{q}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div ref={specDropdownRef} className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Spécialisations (Matières)' : 'Specializations (Subjects)'}</label>
                  <div onClick={() => setShowSpecDropdown(!showSpecDropdown)} className="w-full min-h-[42px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm cursor-pointer flex items-center justify-between hover:border-blue-400 transition-colors">
                    <div className="flex flex-wrap gap-1">
                      {formData.subjects_specialization.length === 0 ? <span className="text-gray-400">{lang === 'fr' ? 'Cliquer pour sélectionner...' : 'Click to select...'}</span> : formData.subjects_specialization.map((s) => (
                        <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium">{s}<button type="button" onClick={(e) => { e.stopPropagation(); toggleSubjectSpecialization(s); }} className="hover:text-purple-900"><X className="w-3 h-3" /></button></span>
                      ))}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showSpecDropdown ? 'rotate-180' : ''}`} />
                  </div>
                  {showSpecDropdown && (
                    <div className="mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                      <div className="p-2">
                        <p className="text-xs text-gray-500 mb-2 px-2">{lang === 'fr' ? 'Sélectionner une ou plusieurs matières' : 'Select one or more subjects'}</p>
                        {subjects.length === 0 ? <p className="text-sm text-gray-400 px-2 py-2">{lang === 'fr' ? 'Aucune matière active' : 'No active subjects'}</p> : subjects.map((subject) => (
                          <label key={subject.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer rounded-lg transition-colors">
                            <input type="checkbox" checked={isSubjectSelected(subject.label_fr)} onChange={() => toggleSubjectSpecialization(subject.label_fr)} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                            <div className="flex-1"><span className="text-sm text-gray-700 font-medium">{subject.label_fr}</span><span className="text-xs text-gray-400 ml-2">({subject.code})</span></div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 7: Photo */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Image className="w-4 h-4 text-indigo-600" />{lang === 'fr' ? 'Photo' : 'Photo'}</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'URL de la photo' : 'Photo URL'}</label>
                  <input value={formData.photo_url} onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="https://..." />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditingTeacher(null) }} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">{lang === 'fr' ? 'Annuler' : 'Cancel'}</button>
                <button type="submit" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md shadow-blue-200">{editingTeacher ? (lang === 'fr' ? 'Mettre à jour' : 'Update') : (lang === 'fr' ? 'Créer' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== DETAIL MODAL (avec tous les nouveaux champs) ==================== */}
      {showDetail && selectedTeacher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDetail(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{lang === 'fr' ? "Profil de l'enseignant" : 'Teacher profile'}</h3>
              <button onClick={() => setShowDetail(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-6">
              {/* Header profil */}
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                  {selectedTeacher.photo_url ? <img src={selectedTeacher.photo_url} alt="" className="w-full h-full object-cover" /> : <>{(selectedTeacher.first_name || selectedTeacher.user?.first_name || '')[0]}{(selectedTeacher.last_name || selectedTeacher.user?.last_name || '')[0]}</>}
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-semibold text-gray-900">{selectedTeacher.last_name || selectedTeacher.user?.last_name} {selectedTeacher.first_name || selectedTeacher.user?.first_name}</h4>
                  <p className="text-sm text-gray-500">{selectedTeacher.matricule}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {getStatusBadge(selectedTeacher.status)}
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{CONTRACT_TYPES.find(ct => ct.value === selectedTeacher.contract_type)?.label}</span>
                  </div>
                </div>
              </div>

              {/* Grid infos */}
              <div className="grid grid-cols-2 gap-6">
                {/* Colonne 1: Identité + Contact */}
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{lang === 'fr' ? 'Identité' : 'Identity'}</h5>
                    <div className="space-y-2">
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Date de naissance:' : 'Date of birth:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.date_of_birth || '-'}</span></p>
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Lieu de naissance:' : 'Place of birth:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.place_of_birth || '-'}</span></p>
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Sexe:' : 'Gender:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.gender === 'M' ? 'Masculin' : selectedTeacher.gender === 'F' ? 'Féminin' : '-'}</span></p>
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Nationalité:' : 'Nationality:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.nationality || '-'}</span></p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{lang === 'fr' ? 'Contact' : 'Contact'}</h5>
                    <div className="space-y-2">
                      <p className="text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{selectedTeacher.email || selectedTeacher.user?.email || '-'}</p>
                      <p className="text-sm flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{selectedTeacher.phone || selectedTeacher.user?.phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Colonne 2: Professionnel + Adresse + Famille */}
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{lang === 'fr' ? 'Professionnel' : 'Professional'}</h5>
                    <div className="space-y-2">
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Profession:' : 'Profession:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.profession || '-'}</span></p>
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Diplôme:' : 'Diploma:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.diploma || '-'}</span></p>
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Spécialité:' : 'Specialty:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.specialty || '-'}</span></p>
                      <p className="text-sm"><span className="text-gray-500">{lang === 'fr' ? 'Embauché le:' : 'Hired on:'}</span> <span className="font-medium text-gray-900">{selectedTeacher.hire_date || '-'}</span></p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{lang === 'fr' ? 'Adresse' : 'Address'}</h5>
                    <div className="space-y-2">
                      <p className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" />{selectedTeacher.neighborhood || '-'}, {selectedTeacher.city || '-'}</p>
                      <p className="text-sm text-gray-700">{selectedTeacher.address || '-'}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{lang === 'fr' ? 'Situation familiale' : 'Family'}</h5>
                    <div className="space-y-2">
                      <p className="text-sm flex items-center gap-2"><Heart className="w-4 h-4 text-gray-400" />{selectedTeacher.marital_status || '-'}</p>
                      <p className="text-sm flex items-center gap-2"><Baby className="w-4 h-4 text-gray-400" />{selectedTeacher.number_of_children || 0} {lang === 'fr' ? 'enfant(s)' : 'child(ren)'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Matières enseignées */}
              <div>
                <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{lang === 'fr' ? 'Matières enseignées' : 'Subjects taught'}</h5>
                <div className="space-y-2">
                  {(!selectedTeacher.teacher_subject_classes || selectedTeacher.teacher_subject_classes.length === 0) ? (
                    <p className="text-sm text-gray-400 italic">{lang === 'fr' ? 'Aucune matière assignée' : 'No subjects assigned'}</p>
                  ) : selectedTeacher.teacher_subject_classes?.map((tsc: any) => (
                    <div key={tsc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-gray-800">{tsc.subject_name || tsc.subject?.label_fr || 'Matière'}</span>
                        <span className="text-xs text-gray-500">— {tsc.class_name || tsc.class_?.name || 'Classe'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">{tsc.hours_per_week}h/semaine</span>
                        <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium flex items-center gap-1"><Calculator className="w-3 h-3" />Coef: {tsc.coefficient || 1}</span>
                        {tsc.is_principal_teacher && <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium flex items-center gap-1"><Star className="w-3 h-3" />Principal</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pièces jointes */}
              {selectedTeacher.attachments && selectedTeacher.attachments.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{lang === 'fr' ? 'Pièces jointes' : 'Attachments'}</h5>
                  <div className="space-y-2">
                    {selectedTeacher.attachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{att.file_name}</span>
                          <span className="text-xs text-gray-400">({formatFileSize(att.file_size)})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== AVAILABILITY MODAL ==================== */}
      {showAvailabilityModal && selectedTeacher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAvailabilityModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><Clock className="w-5 h-5 text-cyan-600" />{lang === 'fr' ? 'Disponibilités' : 'Availabilities'} — {selectedTeacher.last_name} {selectedTeacher.first_name}</h3>
              <button onClick={() => setShowAvailabilityModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500">{lang === 'fr' ? 'Cliquez sur les cases pour marquer les disponibilités (vert = disponible, gris = indisponible)' : 'Click on cells to mark availabilities (green = available, gray = unavailable)'}</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => {
                    const allAvailable: Record<string, boolean> = {}
                    for (let day = 1; day <= 5; day++) {
                      for (const timeSlot of TIME_SLOTS) {
                        allAvailable[`${day}-${timeSlot}`] = true
                      }
                    }
                    setAvailabilityData(allAvailable)
                  }} className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors">
                    {lang === 'fr' ? 'Tout cocher' : 'Check all'}
                  </button>
                  <button type="button" onClick={() => {
                    const allUnavailable: Record<string, boolean> = {}
                    for (let day = 1; day <= 5; day++) {
                      for (const timeSlot of TIME_SLOTS) {
                        allUnavailable[`${day}-${timeSlot}`] = false
                      }
                    }
                    setAvailabilityData(allUnavailable)
                  }} className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors">
                    {lang === 'fr' ? 'Tout décocher' : 'Uncheck all'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-amber-600 mb-4 font-medium">
                {(() => {
                  let count = 0
                  for (let day = 1; day <= 5; day++) {
                    for (const timeSlot of TIME_SLOTS) {
                      const key = `${day}-${timeSlot}`
                      if (availabilityData[key] !== false) count++
                    }
                  }
                  return lang === 'fr' 
                    ? `Créneaux disponibles: ${count} / min 2, max 30` 
                    : `Available slots: ${count} / min 6, max 12`
                })()}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200 rounded-lg">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-2 text-xs font-semibold text-gray-500 border-b border-r">Heure</th>
                      {AVAILABILITY_DAYS.map((day, i) => (
                        <th key={day} className="px-2 py-2 text-xs font-semibold text-gray-500 border-b">{day}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_SLOTS.slice(0, 11).map((timeSlot) => (
                      <tr key={timeSlot} className="border-b border-gray-100">
                        <td className="px-2 py-2 text-xs font-medium text-gray-600 border-r bg-gray-50">{timeSlot}</td>
                        {AVAILABILITY_DAYS.map((_, dayIdx) => {
                          const key = `${dayIdx + 1}-${timeSlot}`
                          const isAvailable = availabilityData[key] !== false
                          return (
                            <td key={dayIdx} className="px-1 py-1">
                              <button
                                type="button"
                                onClick={() => setAvailabilityData(prev => ({ ...prev, [key]: !isAvailable }))}
                                className={`w-full h-8 rounded text-xs font-medium transition-colors ${isAvailable ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                              >
                                {isAvailable ? '✓' : '—'}
                              </button>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => setShowAvailabilityModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">{lang === 'fr' ? 'Annuler' : 'Cancel'}</button>
                <button onClick={saveAvailabilities} className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-cyan-700 text-white rounded-lg text-sm font-medium hover:from-cyan-700 hover:to-cyan-800 transition-all shadow-md shadow-cyan-200">{lang === 'fr' ? 'Enregistrer' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== ATTACHMENT MODAL ==================== */}
      {showAttachmentModal && selectedTeacher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAttachmentModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" />{lang === 'fr' ? 'Pièces jointes' : 'Attachments'} — {selectedTeacher.last_name}</h3>
              <button onClick={() => setShowAttachmentModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-indigo-400 transition-colors">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">{lang === 'fr' ? 'Glissez un fichier ici ou cliquez pour sélectionner' : 'Drag a file here or click to select'}</p>
                <input type="file" onChange={handleFileUpload} className="hidden" id="teacher-file-upload" />
                <label htmlFor="teacher-file-upload" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 cursor-pointer inline-block">
                  {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === 'fr' ? 'Sélectionner un fichier' : 'Select a file')}
                </label>
              </div>
              {selectedTeacher.attachments && selectedTeacher.attachments.length > 0 ? (
                <div className="space-y-2">
                  {selectedTeacher.attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{att.file_name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(att.file_size)})</span>
                      </div>
                      <button onClick={() => deleteAttachment(att.id)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center">{lang === 'fr' ? 'Aucune pièce jointe' : 'No attachments'}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== ASSIGN SUBJECT MODAL (inchangé) ==================== */}
      {showAssign && selectedTeacher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAssign(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">{lang === 'fr' ? 'Assigner une matière' : 'Assign a subject'}</h3>
              <p className="text-sm text-gray-500">{lang === 'fr' ? 'à' : 'to'} {selectedTeacher.last_name} {selectedTeacher.first_name}</p>
            </div>
            <form onSubmit={handleAssignSubject} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Matière *' : 'Subject *'}</label>
                <select required value={assignmentForm.subject_id || ''} onChange={(e) => { const subjectId = parseInt(e.target.value); const subject = subjects.find((s) => s.id === subjectId); setAssignmentForm({ ...assignmentForm, subject_id: subjectId, coefficient: subject?.default_coefficient || 1 }) }} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                  {subjects.map((s) => (<option key={s.id} value={s.id}>{s.code} — {s.label_fr}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Classe *' : 'Class *'}</label>
                <select required value={assignmentForm.class_id || ''} onChange={(e) => { const classId = parseInt(e.target.value); const subject = subjects.find((s) => s.id === assignmentForm.subject_id); const effectiveCoeff = getEffectiveCoefficient(subject, classId); setAssignmentForm({ ...assignmentForm, class_id: classId, coefficient: effectiveCoeff }) }} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                  {classes.map((c) => (<option key={c.id} value={c.id}>{c.name} {c.level_name ? `(${c.level_name})` : ''}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Heures/semaine *' : 'Hours/week *'}</label>
                  <input type="number" step="0.5" min="0.5" required value={assignmentForm.hours_per_week || 2} onChange={(e) => setAssignmentForm({ ...assignmentForm, hours_per_week: parseFloat(e.target.value) })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Coefficient' : 'Coefficient'}</label>
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.5" min="0.5" value={assignmentForm.coefficient || 1} onChange={(e) => setAssignmentForm({ ...assignmentForm, coefficient: parseFloat(e.target.value) })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                    <button type="button" onClick={() => { const subject = subjects.find((s) => s.id === assignmentForm.subject_id); if (subject) openCoeffModal(subject) }} className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100" title={lang === 'fr' ? 'Gérer les coefficients' : 'Manage coefficients'}><Calculator className="w-4 h-4" /></button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{lang === 'fr' ? 'Défaut: ' : 'Default: '}{subjects.find((s) => s.id === assignmentForm.subject_id)?.default_coefficient || 1}</p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" id="principal" checked={assignmentForm.is_principal_teacher || false} onChange={(e) => setAssignmentForm({ ...assignmentForm, is_principal_teacher: e.target.checked })} className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                <label htmlFor="principal" className="text-sm text-gray-700">{lang === 'fr' ? 'Enseignant principal (titulaire)' : 'Principal teacher'}</label>
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAssign(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">{lang === 'fr' ? 'Annuler' : 'Cancel'}</button>
                <button type="submit" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md shadow-blue-200">{lang === 'fr' ? 'Assigner' : 'Assign'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== DELETE CONFIRMATION MODAL ==================== */}
      {showDeleteConfirm && teacherToDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowDeleteConfirm(false); setTeacherToDelete(null) }} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"><Trash2 className="w-5 h-5 text-white" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{lang === 'fr' ? 'Confirmer la suppression' : 'Confirm Deletion'}</h3>
                  <p className="text-sm text-red-100">{lang === 'fr' ? 'Cette action est irréversible' : 'This action cannot be undone'}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-lg font-bold shrink-0">
                    {(teacherToDelete.first_name || teacherToDelete.user?.first_name || '')[0]}{(teacherToDelete.last_name || teacherToDelete.user?.last_name || '')[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{teacherToDelete.last_name || teacherToDelete.user?.last_name} {teacherToDelete.first_name || teacherToDelete.user?.first_name}</p>
                    <p className="text-sm text-gray-500">{teacherToDelete.matricule}</p>
                    <p className="text-sm text-gray-500">{teacherToDelete.email || teacherToDelete.user?.email}</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">{lang === 'fr' ? 'Vous êtes sur le point de supprimer cet enseignant. Toutes les données associées seront également supprimées.' : 'You are about to delete this teacher. All associated data will also be deleted.'}</p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => { setShowDeleteConfirm(false); setTeacherToDelete(null) }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">{lang === 'fr' ? 'Annuler' : 'Cancel'}</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg text-sm font-medium hover:from-red-700 hover:to-red-800 transition-all shadow-md shadow-red-200 flex items-center gap-2"><Trash2 className="w-4 h-4" />{lang === 'fr' ? 'Supprimer définitivement' : 'Delete permanently'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== COEFFICIENT MODAL ==================== */}
      {showCoeffModal && selectedSubject && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCoeffModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><Calculator className="w-5 h-5 text-blue-600" />{lang === 'fr' ? 'Coefficients' : 'Coefficients'} — {selectedSubject.label_fr}</h3>
              <p className="text-sm text-gray-500">{lang === 'fr' ? 'Coefficient défaut:' : 'Default coefficient:'} <strong>{selectedSubject.default_coefficient || 1}</strong></p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">{lang === 'fr' ? 'Ajouter un coefficient spécifique' : 'Add specific coefficient'}</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{lang === 'fr' ? 'Classe' : 'Class'}</label>
                    <select value={newCoeff.class_id || ''} onChange={(e) => setNewCoeff({ ...newCoeff, class_id: parseInt(e.target.value) })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm">
                      <option value="">{lang === 'fr' ? 'Choisir...' : 'Choose...'}</option>
                      {classes.map((c) => (<option key={c.id} value={c.id}>{c.name} {c.level_name ? `(${c.level_name})` : ''}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{lang === 'fr' ? 'Coefficient' : 'Coefficient'}</label>
                    <input type="number" step="0.5" min="0.5" value={newCoeff.coefficient} onChange={(e) => setNewCoeff({ ...newCoeff, coefficient: parseFloat(e.target.value) })} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm" />
                  </div>
                </div>
                <button onClick={handleAddCoefficient} disabled={!newCoeff.class_id} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"><Plus className="w-4 h-4" />{lang === 'fr' ? 'Ajouter' : 'Add'}</button>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">{lang === 'fr' ? 'Coefficients spécifiques enregistrés' : 'Registered specific coefficients'}</h4>
                {subjectCoefficients.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">{lang === 'fr' ? 'Aucun coefficient spécifique' : 'No specific coefficients'}</p>
                ) : (
                  <div className="space-y-2">
                    {subjectCoefficients.map((coeff) => {
                      const cls = classes.find((c) => c.id === coeff.class_id)
                      const isDefault = coeff.coefficient === (selectedSubject.default_coefficient || 1)
                      return (
                        <div key={coeff.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <School className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-700">{cls?.name || `Classe ${coeff.class_id}`}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDefault ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-700'}`}>{isDefault ? (lang === 'fr' ? 'défaut' : 'default') : (lang === 'fr' ? 'spécifique' : 'specific')}: {coeff.coefficient}</span>
                          </div>
                          <button onClick={() => handleDeleteCoefficient(coeff.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button onClick={() => setShowCoeffModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">{lang === 'fr' ? 'Fermer' : 'Close'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== IMPORT EXCEL MODAL ==================== */}
      {showImportModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowImportModal(false); setImportPreview([]); setImportErrors([]) }} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-500 to-purple-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"><FileSpreadsheet className="w-5 h-5 text-white" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{lang === 'fr' ? "Prévisualisation de l'import" : 'Import Preview'}</h3>
                  <p className="text-sm text-indigo-100">{importPreview.length} {lang === 'fr' ? 'enseignants à importer' : 'teachers to import'}{importErrors.length > 0 && ` • ${importErrors.length} ${lang === 'fr' ? 'erreurs' : 'errors'}`}</p>
                </div>
              </div>
              <button onClick={() => { setShowImportModal(false); setImportPreview([]); setImportErrors([]) }} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5 text-white" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {importErrors.length === 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
                  <div><h4 className="text-sm font-semibold text-green-700">{lang === 'fr' ? 'Fichier valide' : 'Valid file'}</h4><p className="text-xs text-green-600">{lang === 'fr' ? 'Toutes les lignes sont prêtes à être importées.' : 'All rows are ready to be imported.'}</p></div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
                    <div><h4 className="text-sm font-semibold text-amber-700">{lang === 'fr' ? `${importErrors.length} erreur(s) détectée(s)` : `${importErrors.length} error(s) found`}</h4><p className="text-xs text-amber-600">{lang === 'fr' ? 'Vous pouvez corriger ou continuer malgré les erreurs.' : 'You can fix or continue despite errors.'}</p></div>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1 bg-white/50 rounded-lg p-2">
                    {importErrors.map((err, idx) => (<p key={idx} className="text-xs text-amber-700 flex items-center gap-1"><XCircle className="w-3 h-3 text-amber-500 shrink-0" />{err}</p>))}
                  </div>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{lang === 'fr' ? 'Aperçu des données' : 'Data preview'}</span>
                  <span className="text-xs text-gray-500">{importPreview.length} {lang === 'fr' ? 'lignes' : 'rows'}</span>
                </div>
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0">
                      <tr className="bg-gray-100 border-b border-gray-200">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase w-10">#</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Nom' : 'Name'}</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Prénom' : 'First'}</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Matières' : 'Subjects'}</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Matricule' : 'Reg. No.'}</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Contrat' : 'Contract'}</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{lang === 'fr' ? 'Statut' : 'Status'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {importPreview.map((row, idx) => (
                        <tr key={idx} className="hover:bg-white/50 transition-colors">
                          <td className="px-3 py-2 text-xs text-gray-400 font-mono">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium text-gray-900">{row.last_name}</td>
                          <td className="px-3 py-2 text-gray-700">{row.first_name}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{row.email}</td>
                          <td className="px-3 py-2">
                            {row.subjects_specialization ? (
                              <div className="flex flex-wrap gap-0.5 max-w-[180px]">
                                {String(row.subjects_specialization).split(/[;,]/).map((s: string) => s.trim()).filter(Boolean).slice(0, 2).map((s: string, i: number) => (
                                  <span key={i} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">{s}</span>
                                ))}
                                {String(row.subjects_specialization).split(/[;,]/).filter((s: string) => s.trim()).length > 2 && (
                                  <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">+{String(row.subjects_specialization).split(/[;,]/).filter((s: string) => s.trim()).length - 2}</span>
                                )}
                              </div>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2"><span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{row.matricule}</span></td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{row.contract_type}</span></td>
                          <td className="px-3 py-2">{getStatusBadge(row.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
              <div className="text-xs text-gray-500"><span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" />{lang === 'fr' ? 'Les enseignants seront créés avec un compte utilisateur automatique.' : 'Teachers will be created with an automatic user account.'}</span></div>
              <div className="flex gap-3">
                <button onClick={() => { setShowImportModal(false); setImportPreview([]); setImportErrors([]) }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">{lang === 'fr' ? 'Annuler' : 'Cancel'}</button>
                <button onClick={handleImportConfirm} disabled={importLoading || importPreview.length === 0} className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                  {importLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> {lang === 'fr' ? 'Import en cours...' : 'Importing...'}</> : <><Upload className="w-4 h-4" /> {lang === 'fr' ? "Confirmer l'import" : 'Confirm Import'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}